<template>
  <ItemPage
    :title="!id? 'Criar questão' : `Editando questão ${this.$route.params.id}`"
    size="lg"
    :actions="actions"
  >
    <v-progress-linear
      v-if="loading"
      color="amber"
      indeterminate
    />
    <v-alert
      v-if="published"
      dense
      type="info"
    >
      Esta avaliação está <strong>Publicada </strong> e por isso não pode ser <strong>atualizada</strong> ou <strong>excluida</strong>
    </v-alert>

    <div v-show="!loading">
      <div class="my-4">
        Tópico: {{ topicName }}
      </div>

      <div class="title">
        Enunciado
      </div>
      <div class="editor elevation-2 mt-3 white">
        <editor-menu-bar
          v-slot="{ commands, isActive, getNodeAttrs }"
          :editor="statementEditor"
        >
          <v-toolbar
            flat
            dense
          >
            <v-btn
              :disabled="published"
              icon
              tile
              :input-value="isActive.bold()"
              :color="isActive.bold() ? 'primary' : ''"
              @click="commands.bold()"
            >
              <v-icon>
                mdi-format-bold
              </v-icon>
            </v-btn>

            <v-btn
              :disabled="published"
              icon
              tile
              :input-value="isActive.italic()"
              :color="isActive.italic() ? 'primary' : ''"
              @click="commands.italic()"
            >
              <v-icon>
                mdi-format-italic
              </v-icon>
            </v-btn>

            <v-btn
              :disabled="published"
              icon
              tile
              :input-value="isActive.aude_question_image()"
              :color="isActive.aude_question_image() ? 'primary' : ''"
              @click="handleImageCommand(commands.aude_question_image)"
            >
              <v-icon>
                mdi-image
              </v-icon>
            </v-btn>

            <v-btn
              :disabled="published"
              icon
              tile
              :input-value="isActive.aude_question_katex()"
              :color="isActive.aude_question_katex() ? 'primary' : ''"
              @click="handleKatexCommand(commands, isActive.aude_question_katex(), getNodeAttrs)"
            >
              <v-icon>
                mdi-square-root
              </v-icon>
            </v-btn>
          </v-toolbar>
        </editor-menu-bar>
        <v-divider />
        <div>
          <editor-content
            class="editor-content pa-5"
            :editor="statementEditor"
          />
        </div>
      </div>
      <div class="mt-12 d-flex align-center">
        <div class="title">
          Respostas
        </div>

        <v-spacer />

        <v-btn
          :disabled="published"
          color="primary"
          text
          @click="addAnswer"
        >
          <v-icon left>
            mdi-plus
          </v-icon>
          Adicionar resposta
        </v-btn>
      </div>

      <template v-if="answers.length > 0">
        <div
          v-for="(answer, index) in answers"
          :key="answer.id"
          class="mt-8"
        >
          <div class="d-flex justify-space-between">
            <div>Resposta {{ index + 1 }}</div>
            <v-btn
              v-if="correctAnswerId !== answer.id"
              :disabled="published"
              class="text-decoration-none"
              text
              @click="setCorrectAnswer(answer.id)"
            >
              Marcar como correta
            </v-btn>

            <div
              v-else
              class="success--text"
              style="min-height: 36px;"
            >
              <v-icon color="success">
                mdi-check
              </v-icon>
              Correta
            </div>
          </div>
          <div class="mt-2 editor elevation-2 white">
            <editor-menu-bar
              v-slot="{ commands, isActive, getNodeAttrs }"
              :editor="answer.editor"
            >
              <v-toolbar
                flat
                dense
              >
                <v-btn
                  :disabled="published"
                  icon
                  tile
                  :input-value="isActive.bold()"
                  :color="isActive.bold() ? 'primary' : ''"
                  @click="commands.bold()"
                >
                  <v-icon>
                    mdi-format-bold
                  </v-icon>
                </v-btn>

                <v-btn
                  :disabled="published"
                  icon
                  tile
                  :input-value="isActive.italic()"
                  :color="isActive.italic() ? 'primary' : ''"
                  @click="commands.italic()"
                >
                  <v-icon>
                    mdi-format-italic
                  </v-icon>
                </v-btn>

                <v-btn
                  :disabled="published"
                  icon
                  tile
                  :input-value="isActive.aude_question_image()"
                  :color="isActive.aude_question_image() ? 'primary' : ''"
                  @click="handleImageCommand(commands.aude_question_image)"
                >
                  <v-icon>
                    mdi-image
                  </v-icon>
                </v-btn>

                <v-btn
                  :disabled="published"
                  icon
                  tile
                  :input-value="isActive.aude_question_katex()"
                  :color="isActive.aude_question_katex() ? 'primary' : ''"
                  @click="handleKatexCommand(commands, isActive.aude_question_katex(), getNodeAttrs)"
                >
                  <v-icon>
                    mdi-square-root
                  </v-icon>
                </v-btn>

                <v-spacer />

                <v-btn
                  :disabled="published"
                  icon
                  tile
                  @click="showRemoveAnswerModal(index)"
                >
                  <v-icon>
                    mdi-delete
                  </v-icon>
                </v-btn>
              </v-toolbar>
            </editor-menu-bar>
            <v-divider />
            <div>
              <editor-content
                class="editor-content pa-5"
                :editor="answer.editor"
              />
            </div>
          </div>
        </div>
      </template>
      <div
        v-else
        class="grey--text text-center my-5"
      >
        Nenhuma reposta.
      </div>
    </div>
  </ItemPage>
</template>

<script>
import KatexDialog from '@/components/KatexDialog.vue'
import ImageDialog from '@/components/ImageDialog.vue'
import ItemPage from '@/components/pages/ItemPage.vue'
import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
import {
  HardBreak,
  Bold,
  Italic,
  History
} from 'tiptap-extensions'

import Image from '@/components/contents/questions/editor/nodes/Image'
import Katex from '@/components/contents/questions/editor/nodes/Katex'
import { makeid } from '@/util.js'

export default {
  components: {
    EditorContent,
    EditorMenuBar,
    ItemPage
  },

  data: () => ({
    question: null,
    statementEditor: new Editor({
      extensions: [
        new HardBreak(),
        new Image(),
        new Bold(),
        new Italic(),
        new History(),
        new Katex()
      ],
      content: {
        type: 'doc',
        content: [
          {
            type: 'paragraph'
          }
        ]
      }
    }),
    answers: [],
    imageCommand: null,

    // Katex extension
    katexCommand: null,
    katexInput: null,
    katexUpdating: false,
    invalidKatex: true,
    katexPreview: '',

    selectedImage: null,
    isFetching: true,
    correctAnswerId: null,
    loading: false,
    foreign_language: 0,
    foreign_language_items: [
      {
        name: 'Nenhuma',
        value: 0
      },
      {
        name: 'Língua inglesa',
        value: 1
      },
      {
        name: 'Língua espanhola',
        value: 2
      }
    ],
    topicName: ''
  }),
  computed: {
    idgroup () {
      return this.$route.params.idgroup
    },
    status () {
      return this.$route.params.status
    },
    gradeLevel () {
      return this.$route.params.gradelevel
    },
    id () {
      return this.$route.params.id
    },
    published () {
      if (this.status === '1' || this.status === 1) {
        return true
      }
      return false
    },

    actions () {
      return [
        {
          label: this.id ? 'Atualizar' : 'Salvar',
          action: this.submit,
          icon: 'mdi-floppy',
          disabled: this.published ? this.published : this.loading
        },
        {
          label: 'Deletar',
          action: this.deleteQuestions,
          icon: 'mdi-delete',
          disabled: this.published
        }
      ]
    }
  },

  async mounted () {
    if (!this.id) {
      return
    }

    this.loading = true

    const res = await this.$api.getAudeExamQuestion(this.id)
    this.question = res
    this.foreign_language = res.foreign_language

    await this.getTopic(res.topic_id)

    const textData = JSON.parse(res.text_data)

    this.statementEditor.setOptions({
      editable: !this.published
    })

    this.statementEditor.setContent(textData.statement, true)

    this.correctAnswerId = textData.correctAnswerId

    for (const answer of textData.answers) {
      answer.editor = new Editor({
        editable: !this.published,
        extensions: [
          new HardBreak(),
          new Image(),
          new Bold(),
          new Italic(),
          new History(),
          new Katex()
        ],
        content: answer.state
      })

      answer.isCorrect = textData.correnctAnswerId && (textData.correnctAnswerId === answer.id)
      delete answer.state

      if (!answer.id) {
        answer.id = makeid()
      }

      this.answers.push(answer)
    }

    this.loading = false
  },

  beforeDestroy () {
    this.statementEditor.destroy()
    for (const answer of this.answers) {
      answer.editor.destroy()
    }
  },

  methods: {
    // Image Extension
    handleImageCommand (command) {
      this.openImageDialog()
      this.imageCommand = command
    },

    openImageDialog () {
      this.$dialog.custom(ImageDialog).show()
        .then(async (data) => {
          if (!data) {
            return
          }
          try {
            const uploadedImageData = await this.$api.uploadAudeQuestionImage(this.id, data.image, data.size)
            this.imageCommand({
              src: uploadedImageData.url,
              audeQuestionImageId: uploadedImageData.id
            })
          } catch {

          }
        })
    },

    // Katex Extension
    handleKatexCommand (commands, isActive, getNodeAttrs) {
      if (!isActive) {
        this.katexUpdating = false
        this.katexInput = ''
        this.katexCommand = commands.aude_question_katex_createNode
      } else {
        this.katexUpdating = true
        const { expression } = getNodeAttrs('aude_question_katex')
        this.katexInput = expression
        this.katexCommand = commands.aude_question_katex_updateNode
      }
      this.openKatexDialog()
    },

    openKatexDialog () {
      this.$dialog.custom(KatexDialog).with({ kInput: this.katexInput }).show()
        .then((katex) => {
          if (!katex) {
            return
          }
          this.onSaveKatexClick(katex)
        })
    },

    onSaveKatexClick (katex) {
      this.katexCommand({
        expression: katex
      })
    },

    addAnswer () {
      const editor = new Editor({
        extensions: [
          new HardBreak(),
          new Image(),
          new Bold(),
          new Italic(),
          new History(),
          new Katex()
        ],
        content: {
          type: 'doc',
          content: [
            {
              type: 'paragraph'
            }
          ]
        }
      })

      this.answers.push(
        {
          id: makeid(),
          isCorrect: false,
          editor
        })
    },

    async showRemoveAnswerModal (index) {
      this.$dialog.confirm('Remover resposta')
        .text('Tem certeza que deseja remover esta resposta?')
        .label('Remover')
        .onConfirm(() => {
          this.removeAnswer(index)
        })
        .show()
    },

    removeAnswer (index) {
      if (this.answers[index].id === this.correctAnswerId) {
        this.correctAnswerId = null
      }

      this.answers[index].editor.destroy()
      this.answers.splice(index, 1)
    },

    isCorrect (id) {
      return this.correctAnswerId === id
    },

    setCorrectAnswer (id) {
      this.correctAnswerId = id
    },

    async submit () {
      if (!this.answers.length) {
        this.$store.dispatch('setSnackBar', { text: 'Adicione ao menos uma resposta.', color: 'red' })
        return
      }

      if (!this.correctAnswerId) {
        this.$store.dispatch('setSnackBar', { text: 'Marque a resposta correta.', color: 'red' })
        return
      }

      const imageIds = []

      this.statementEditor.state.doc.descendants(function (node) {
        if (node.type.name === 'aude_question_image') {
          imageIds.push(node.attrs.audeQuestionImageId)
        }
      })

      for (const answer of this.answers) {
        answer.editor.state.doc.descendants(function (node) {
          if (node.type.name === 'aude_question_image') {
            imageIds.push(node.attrs.audeQuestionImageId)
          }
        })
      }

      const textData = {
        statement: this.statementEditor.getJSON(),
        answers: [],
        correctAnswerId: this.correctAnswerId
      }
      for (const answer of this.answers) {
        textData.answers.push({
          id: answer.id,
          state: answer.editor.getJSON()
        })
      }

      const data = {
        text_data: JSON.stringify(textData),
        images_ids: imageIds,
        foreign_language: this.foreign_language
      }

      try {
        if (this.id) {
          await this.$api.updateAudeExamQuestion(this.id, data)
          this.$store.dispatch('setSnackBar', { text: 'Questão atualizada com sucesso.' })
        } else {
          await this.$api.newAudeExamQuestions(this.idgroup, data)
          this.$store.dispatch('setSnackBar', { text: 'Questão criada com sucesso.' })
        }
        this.$router.go(-1)
      } catch (e) {
        this.$store.dispatch('setSnackBar', { text: 'Ocorreu um erro ao tentar criar a questão.', color: 'red' })
      }
    },

    async deleteQuestions () {
      this.$dialog.confirm('Apagar Questão?')
        .text('Essa ação não pode ser desfeita')
        .label('Apagar')
        .onConfirm(async () => {
          this.loading = true
          try {
            await this.$api.deleteAudeExamQuestion(this.id)
            this.$store.dispatch('setSnackBar', { text: 'Questão excluída com sucesso.' })
            this.loading = false
            this.$router.go(-1)
          } catch (e) {
            this.$store.dispatch('setSnackBar', { text: 'Erro ao tentar excluir questão.', color: 'red' })
            this.loading = false
          }
        })
        .show()
    },

    async getTopic (topicID) {
      if (!topicID) {
        this.topicName = 'Sem tópico'
      }

      try {
        const response = await this.$api.getTopic(topicID)
        this.topic = response
        this.topicName = this.topic.name
      } catch {
      }
    }
  }
}
</script>
